import { useQuery } from "@tanstack/react-query";
import Link from "next/link";

import Image from "next/image";
import Layout from "../../components/layouts/layout.comp";
import { useTranslation } from "../../core/translation.core";
import LocalePs from "../../components/locale/localePs.core";
import { ListSection, Tag } from "../../components/reuseables/list.comp";
import localeCore from "../../core/locale.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import AddLink from "../../components/form/addLink.comp";
import { GiftCardDef } from "../../types/giftCard";
import giftCardCore from "../../core/giftCard.core";

export default function GiftCardsPage() {
  const { t, msg } = useTranslation();

  return (
    <Layout title={t(msg.giftCard.giftCards)} titleBtn={<AddLink id="giftCard-create" href="/giftCard/defCreate" />}>
      <ListSection>
        <GiftCardsList />
      </ListSection>
    </Layout>
  );
}

const GiftCardsList = () => {
  const { t, msg } = useTranslation();
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["giftCards"],
    queryFn: giftCardCore.loadGiftCardDefs,
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isPending) return <LoadingOvalCenterIcon />;

  if (!data) return null;

  return (
    <div id="gift-card-list" className="pt-6 lg:pt-0">
      <div className="hidden lg:grid lg:grid-cols-[3fr_3fr_3fr_3fr_2fr_1fr] gap-6 px-6 py-3 text-sm font-medium">
        <div>{t(msg.product.image)}</div>
        <div>{t(msg.general.name)}</div>
        <div>{t(msg.product.sku)}</div>
        <div>{t(msg.membership.activationMethod)}</div>
        <div></div>
      </div>

      {data!.map((a) => (
        <Row key={a.id} giftCardDef={a} />
      ))}
    </div>
  );
};

function Row({ giftCardDef }: { giftCardDef: GiftCardDef }) {
  const { t, msg } = useTranslation();

  return (
    <div className="membership-row grid grid-cols-2 lg:grid-cols-[3fr_3fr_3fr_3fr_2fr_1fr] gap-x-6 gap-y-3 px-6 py-6 border-t-[1px] text-sm group/edit">
      <div className={`row-span-5 lg:row-span-2 ${giftCardDef.product.status !== "active" ? "opacity-25" : ""}`}>
        {giftCardDef.product.featureMedia ? (
          <Image
            unoptimized
            src={giftCardDef.product.featureMedia.thumbnailUrl}
            width={100}
            height={100}
            alt={localeCore.getLocaleValueDefault(giftCardDef.product.name) ?? ""}
          />
        ) : null}
      </div>
      <div data-value={localeCore.getLocaleValueDefault(giftCardDef.name)}>
        <Tag>{t(msg.general.name)}</Tag>
        <LocalePs value={giftCardDef.name} />
      </div>
      <div data-value={localeCore.getLocaleValueDefault(giftCardDef.name)}>
        <Tag>{t(msg.product.sku)}</Tag>
        <p>{giftCardDef.product.sku}</p>
      </div>
      <div data-value={giftCardDef.issueMode}>
        <Tag>{t(msg.membership.activationMethod)}</Tag>
        <LabelIssueMode giftCardDef={giftCardDef} />
      </div>

      <div className="col-span-2 lg:col-span-1 md:opacity-0 md:group-hover/edit:opacity-100">
        <Link className="membership-edit underline" href={`/giftCard/defEdit/${giftCardDef.id}`}>
          {t(msg.form.edit)}
        </Link>
      </div>
    </div>
  );
}

function LabelIssueMode({ giftCardDef }: { giftCardDef: GiftCardDef }) {
  const { t, msg } = useTranslation();

  switch (giftCardDef.issueMode) {
    case "auto":
      return <p>{t(msg.product.auto)}</p>;
    case "manual":
      return <p>{t(msg.product.manual)}</p>;
    default:
      return null;
  }
}
